import { useEffect, useState } from 'react';
import { serviceArt } from '../v2/kiosk/art';
import type { AppLang } from './ClientApp';

const INTL: Record<AppLang, string> = { pt: 'pt-PT', en: 'en-GB', fr: 'fr-FR', es: 'es-ES', ru: 'ru-RU' };

/**
 * «O meu abonamento» in the client app (owner 26.10.2026): a fifth card on the home screen for a
 * client with a membership in use, a page with the sessions left and the treatments it covers, and
 * «Book with my membership», which opens the booking with those treatments on top.
 * Read-only from Altegio through /v1/me/memberships; needs the sign-in of «My bookings».
 */
export interface AppMembership {
  id?: string;
  title: string;
  status: string;
  expiresAt?: string;
  frozen: boolean;
  visitsLeft?: number;
  unlimited: boolean;
  services: Array<{ title: string; visitsLeft?: number; unlimited: boolean }>;
  studio: string;
  /** Hub service ids it covers, per studio id. */
  serviceIds?: Record<string, string[]>;
}

const TOKEN_KEY = 'hub-me-token';

function token(): string | undefined {
  try {
    return window.localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/** The signed-in client's memberships still in use; empty without sign-in, while loading or on error. */
export function useMyMemberships(fetchImpl: typeof fetch): AppMembership[] {
  const [list, setList] = useState<AppMembership[]>([]);
  useEffect(() => {
    const t = token();
    if (!t) return;
    let live = true;
    fetchImpl('/v1/me/memberships', { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((b: { data?: { memberships?: AppMembership[] } } | undefined) => {
        if (live) setList(b?.data?.memberships ?? []);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [fetchImpl]);
  return list;
}

/** Sessions left in total (shared balance, or the sum of per-treatment balances). */
export function sessionsLeft(m: AppMembership): number | undefined {
  if (m.unlimited) return undefined;
  if (m.visitsLeft !== undefined) return m.visitsLeft;
  const counted = m.services.filter((x) => x.visitsLeft !== undefined);
  return counted.length ? counted.reduce((n, x) => n + (x.visitsLeft ?? 0), 0) : undefined;
}

/** «10 sessões» in the title → 10; the dots need the size of the pack. */
function packSize(title: string, left?: number): number | undefined {
  const n = Number(/(\d{1,2})\s*(?:sess|session|x\b|pack)/i.exec(title)?.[1] ?? /pack-?(\d{1,2})/i.exec(title)?.[1]);
  if (Number.isFinite(n) && n > 0 && (left === undefined || n >= left)) return n;
  return undefined;
}

function day(iso: string, lang: AppLang): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(INTL[lang], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y!, m! - 1, d!)));
}

const M = {
  pt: {
    card: 'O meu abonamento',
    left: (n: number) => `${n} ${n === 1 ? 'restante' : 'restantes'}`,
    unlimited: 'Ilimitado',
    title: 'O meu abonamento',
    label: 'Abonamento',
    of: (n: number, all?: number) =>
      all ? `${n} de ${all} restantes` : `${n} ${n === 1 ? 'sessão restante' : 'sessões restantes'}`,
    until: (d: string) => `Válido até ${d}`,
    paused: 'Congelado',
    included: 'Incluído no abonamento',
    yes: '✓ incluído',
    book: 'Marcar com abonamento',
    note: 'Uma sessão é descontada depois da visita.',
    back: 'Voltar',
  },
  en: {
    card: 'My membership',
    left: (n: number) => `${n} left`,
    unlimited: 'Unlimited',
    title: 'My membership',
    label: 'Membership',
    of: (n: number, all?: number) => (all ? `${n} of ${all} left` : `${n} ${n === 1 ? 'session' : 'sessions'} left`),
    until: (d: string) => `Valid until ${d}`,
    paused: 'Paused',
    included: 'Included in your membership',
    yes: '✓ included',
    book: 'Book with my membership',
    note: 'One session is deducted after the visit.',
    back: 'Back',
  },
  fr: {
    card: 'Mon abonnement',
    left: (n: number) => `${n} restante${n === 1 ? '' : 's'}`,
    unlimited: 'Illimité',
    title: 'Mon abonnement',
    label: 'Abonnement',
    of: (n: number, all?: number) =>
      all ? `${n} sur ${all} restantes` : `${n} séance${n === 1 ? '' : 's'} restante${n === 1 ? '' : 's'}`,
    until: (d: string) => `Valable jusqu’au ${d}`,
    paused: 'En pause',
    included: 'Inclus dans votre abonnement',
    yes: '✓ inclus',
    book: 'Réserver avec mon abonnement',
    note: 'Une séance est déduite après la visite.',
    back: 'Retour',
  },
  es: {
    card: 'Mi bono',
    left: (n: number) => `${n} restante${n === 1 ? '' : 's'}`,
    unlimited: 'Ilimitado',
    title: 'Mi bono',
    label: 'Bono',
    of: (n: number, all?: number) =>
      all ? `${n} de ${all} restantes` : `${n} sesión${n === 1 ? '' : 'es'} restante${n === 1 ? '' : 's'}`,
    until: (d: string) => `Válido hasta ${d}`,
    paused: 'En pausa',
    included: 'Incluido en su bono',
    yes: '✓ incluido',
    book: 'Reservar con mi bono',
    note: 'Se descuenta una sesión después de la visita.',
    back: 'Atrás',
  },
  ru: {
    card: 'Мой абонемент',
    left: (n: number) => `осталось ${n}`,
    unlimited: 'Безлимит',
    title: 'Мой абонемент',
    label: 'Абонемент',
    of: (n: number, all?: number) => (all ? `осталось ${n} из ${all}` : `осталось сеансов: ${n}`),
    until: (d: string) => `Действует до ${d}`,
    paused: 'Заморожен',
    included: 'Входит в абонемент',
    yes: '✓ входит',
    book: 'Записаться по абонементу',
    note: 'Сеанс списывается после визита.',
    back: 'Назад',
  },
} as const;

/** The dark card at the top of the home screen. */
/**
 * The dark membership card with the brand mark (owner 26–27.10.2026): on the home screen it opens the
 * membership page; in «My bookings» it only shows (no onOpen).
 */
export function MembershipCard({ m, lang, onOpen }: { m: AppMembership; lang: AppLang; onOpen?: () => void }) {
  const t = M[lang];
  const left = sessionsLeft(m);
  const body = (
    <>
      <img className="ca3-mem-mark" src="/v3/mark-white.png" alt="" />
      <span>
        <b>{t.card}</b>
        <small>
          {m.title}
          {m.expiresAt ? ` · ${t.until(day(m.expiresAt, lang))}` : ''}
          {m.frozen ? ` · ${t.paused}` : ''}
        </small>
      </span>
      <em className="ca3-mem-pill">{m.unlimited ? t.unlimited : left !== undefined ? t.left(left) : ''}</em>
    </>
  );
  return onOpen ? (
    <button type="button" className="ca3-card ca3-mem" onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="ca3-card ca3-mem" role="group" aria-label={t.card}>
      {body}
    </div>
  );
}

/** The membership page: sessions, validity, the treatments it covers, «Book with my membership». */
export function MembershipPage({
  m,
  lang,
  onBook,
  onBack,
}: {
  m: AppMembership;
  lang: AppLang;
  onBook: () => void;
  onBack: () => void;
}) {
  const t = M[lang];
  const left = sessionsLeft(m);
  const all = packSize(m.title, left);
  return (
    <main className="ca3">
      <header className="ca3-top">
        <a className="ca3-logo" href="/app" aria-label="360°FACE">
          360<i>°</i>FACE
        </a>
      </header>
      <h1 className="ca3-h1">{t.title}</h1>
      <section className="ca3-memcard" aria-label={t.label}>
        <img className="ca3-memart" src="/v3/mark-white.png" alt="" />
        <small>{t.label}</small>
        <b>{m.title}</b>
        {all !== undefined && left !== undefined && (
          <span className="ca3-dots" aria-hidden>
            {Array.from({ length: all }, (_, i) => (
              <i key={i} className={i < all - left ? 'used' : undefined} />
            ))}
          </span>
        )}
        <span className="ca3-memfoot">
          <span>{m.unlimited ? t.unlimited : left !== undefined ? t.of(left, all) : ''}</span>
          {m.expiresAt && <span>{t.until(day(m.expiresAt, lang))}</span>}
        </span>
        {m.frozen && <span className="ca3-memfoot">{t.paused}</span>}
      </section>
      {m.services.length > 0 && (
        <>
          <h2 className="ca3-sec">{t.included}</h2>
          <ul className="ca3-incl">
            {m.services.map((s, i) => (
              <li key={i}>
                {serviceArt({ pt: s.title }) && <img src={serviceArt({ pt: s.title })} alt="" />}
                <span>{s.title}</span>
                <em>{s.visitsLeft !== undefined ? t.left(s.visitsLeft) : t.yes}</em>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="ca3-rail ca3-rail-col">
        <button type="button" className="ca3-btn red" onClick={onBook} disabled={m.frozen}>
          {t.book}
        </button>
        <p className="ca3-note">{t.note}</p>
        <button type="button" className="ca3-btn" onClick={onBack}>
          {t.back}
        </button>
      </div>
    </main>
  );
}

/** Extra query for /v3 when booking with a membership: its name and the covered services of the studio. */
export function membershipQuery(m: AppMembership, studio: string): string {
  const ids = m.serviceIds?.[studio] ?? [];
  return `&mem=${encodeURIComponent(m.title)}${ids.length ? `&incl=${ids.map(encodeURIComponent).join(',')}` : ''}`;
}
